import { lazy, memo, Suspense } from 'react';

import { FormLabeledSwitch } from '@components/form/form-labeled-switch';
import { Spinner } from '@components/loading-spinner';
import { Stack } from '@components/stack';
import { useEvent } from '@hooks/use-event';
import { Spacing } from '@styles/theme';

import { FormActions } from './form-actions';
import { FormCheckbox } from './form-checkbox';
import { FormCheckboxGroup } from './form-checkbox-group';
import { FormConditional } from './form-conditional';
import { FormConditionalValue } from './form-conditional-value';
import { FormDatePicker } from './form-datepicker';
import { FormDesignAvatarChooserField } from './form-design-avatar-chooser';
import { FormEmailField } from './form-email-field';
import { FormEmailListField } from './form-email-list-field';
import { FormEmailTextField } from './form-email-text-field';
import { FormErrors } from './form-errors';
import { FormFieldMappingDatePicker } from './form-field-mapping-date-picker';
import { FormFieldMappingField } from './form-field-mapping-field';
import { FormFieldMappingFileField } from './form-field-mapping-file-field';
import { FormFieldMappingFileListField } from './form-field-mapping-file-list-field';
import { FormFieldMappingJsonEditor } from './form-field-mapping-json-editor';
import { FormFieldMappingListField } from './form-field-mapping-list-field';
import { FormFieldMappingNumberField } from './form-field-mapping-number-field';
import { FormFieldMappingPhoneField } from './form-field-mapping-phone-field';
import { FormFieldMappingPhoneListField } from './form-field-mapping-phone-list-field';
import { FormFieldMappingSelectField } from './form-field-mapping-select-field';
import { FormFieldMappingSelectMulti } from './form-field-mapping-select-multi';
import { FormFieldMappingTextEditor } from './form-field-mapping-text-editor';
import { FormFileDropzone } from './form-file-dropzone';
import { FormFileField } from './form-file-field';
import { FormFileFieldMulti } from './form-file-field-multi';
import { FormGraphQLEditor } from './form-graphql-editor';
import { FormHiddenField } from './form-hidden-field';
import { FormIconChooser } from './form-icon-chooser';
import { FormInstantUploadFileField } from './form-instant-upload-file-field';
import { FormInstantUploadLogoField } from './form-instant-upload-logo-field';
import { FormInstantUploadMultiFileField } from './form-instant-upload-multi-file-field';
import { FormJsonEditor } from './form-json-editor';
import { FormLogoField } from './form-logo-field';
import { FormNumberField } from './form-number-field';
import { FormPasswordField } from './form-password-field';
import { FormPhoneField } from './form-phone-field';
import { FormPrefixedPostfixedTextField } from './form-prefixed-postfixed-text-field';
import { FormProvider, FormProviderProps } from './form-provider';
import { FormPythonEditor } from './form-python-editor';
import { FormRadioGroup } from './form-radio-group';
import { FormReset } from './form-reset';
import { FormSelect } from './form-select';
import { FormSelectMulti } from './form-select-multi';
import { FormSlider } from './form-slider';
import { FormSubmit } from './form-submit';
import { FormSwitch } from './form-switch';
import { FormTextEditor } from './form-text-editor';
import { FormTextField } from './form-text-field';
import { FormTextarea } from './form-textarea';
import { FormUrlField } from './form-url-field';
import { FormWeekdayPicker } from './form-weekday-picker';
import { FormTokenListField } from './token-list-field';
// FormDynamicFields will end up circular referencing because
// createForm assigns Form.DynamicFields as FormDynamicFields
// but FormDynamicFields then tries to use the exported Form
// created by createForm().
const FormDynamicFields = lazy(() => import('./dynamic-fields'));

export type FormProps<T = any> = FormProviderProps<T> & {
  // Gap between the form's direct children. Pass 0 when those children are a
  // modal's sections: they carry their own padding, so a gap here doubles the
  // space under the header and above the footer.
  space?: Spacing;
};

export function createForm<T = any>(defaultProps?: Partial<FormProps<T>>) {
  const Form = (formProps: FormProps<T>) => {
    const props = { ...defaultProps, ...formProps };
    const {
      children,
      onSubmit,
      onSubmitSuccess,
      updateErrors,
      overrideErrors,
      updateValues,
      overrideValues,
      space = 4,
      ...rest
    } = props;
    const onSubmitEvent = useEvent(onSubmit);
    const onSubmitSuccessEvent = useEvent(onSubmitSuccess);

    return (
      <FormProvider
        onSubmit={onSubmitEvent}
        onSubmitSuccess={onSubmitSuccessEvent}
        updateErrors={updateErrors}
        updateValues={updateValues}
        overrideErrors={overrideErrors}
        overrideValues={overrideValues}
        {...rest}
      >
        <Suspense fallback={<Spinner />}>
          <Stack space={space}>{children}</Stack>
        </Suspense>
      </FormProvider>
    );
  };

  Form.Errors = FormErrors;
  Form.Actions = FormActions;
  Form.Conditional = FormConditional;
  Form.ConditionalValue = FormConditionalValue;
  Form.Reset = FormReset;
  Form.Submit = FormSubmit;

  Form.Checkbox = memo(FormCheckbox<T>);
  Form.Checkbox.displayName = 'Form.Checkbox';

  Form.CheckboxGroup = memo(FormCheckboxGroup<T>);
  Form.CheckboxGroup.displayName = 'Form.CheckboxGroup';

  Form.DatePicker = memo(FormDatePicker<T>);
  Form.DatePicker.displayName = 'Form.DatePicker';

  Form.FileField = memo(FormFileField<T>);
  Form.FileField.displayName = 'Form.FileField';

  Form.FileDropzone = memo(FormFileDropzone<T>);
  Form.FileDropzone.displayName = 'Form.FileDropzone';

  Form.HiddenField = memo(FormHiddenField<T>);
  Form.HiddenField.displayName = 'Form.HiddenField';

  Form.InstantUploadFileField = memo(FormInstantUploadFileField<T>);
  Form.InstantUploadFileField.displayName = 'Form.FormInstantUploadFileField';

  Form.InstantUploadMultiFileField = memo(FormInstantUploadMultiFileField<T>);
  Form.InstantUploadMultiFileField.displayName = 'Form.FormInstantUploadMultiFileField';

  Form.IconChooser = memo(FormIconChooser<T>);
  Form.IconChooser.displayName = 'Form.IconChooser';

  Form.DesignAvatarChooserField = memo(FormDesignAvatarChooserField<T>);
  Form.DesignAvatarChooserField.displayName = 'Form.DesignAvatarChooserField';

  Form.LogoField = memo(FormLogoField<T>);
  Form.LogoField.displayName = 'Form.LogoField';

  Form.InstantUploadLogoField = memo(FormInstantUploadLogoField<T>);
  Form.InstantUploadLogoField.displayName = 'Form.InstantUploadLogoField';

  Form.FileFieldMulti = memo(FormFileFieldMulti<T>);
  Form.FileFieldMulti.displayName = 'Form.FileFieldMulti';

  Form.PasswordField = memo(FormPasswordField<T>);
  Form.PasswordField.displayName = 'Form.PasswordField';

  Form.RadioGroup = memo(FormRadioGroup<T>);
  Form.RadioGroup.displayName = 'Form.RadioGroup';

  Form.Select = memo(FormSelect<T>);
  Form.Select.displayName = 'Form.Select';

  Form.SelectMulti = memo(FormSelectMulti<T>);
  Form.SelectMulti.displayName = 'Form.SelectMulti';

  Form.Switch = memo(FormSwitch<T>);
  Form.Switch.displayName = 'Form.Switch';

  Form.Slider = memo(FormSlider<T>);
  Form.Slider.displayName = 'Form.Slider';

  Form.WeekdayPicker = memo(FormWeekdayPicker<T>);
  Form.WeekdayPicker.displayName = 'Form.WeekdayPicker';

  Form.LabeledSwitch = memo(FormLabeledSwitch<T>);
  Form.LabeledSwitch.displayName = 'Form.LabeledSwitch';

  Form.TextField = memo(FormTextField<T>);
  Form.TextField.displayName = 'Form.TextField';

  Form.NumberField = memo(FormNumberField<T>);
  Form.NumberField.displayName = 'Form.NumberField';

  Form.PhoneField = memo(FormPhoneField<T>);
  Form.PhoneField.displayName = 'Form.PhoneField';

  Form.Textarea = memo(FormTextarea<T>);
  Form.Textarea.displayName = 'Form.Textarea';

  Form.TextEditor = memo(FormTextEditor<T>);
  Form.TextEditor.displayName = 'Form.TextEditor';

  Form.TokenListField = memo(FormTokenListField<T>);
  Form.TokenListField.displayName = 'Form.TokenListField';

  Form.EmailField = memo(FormEmailField<T>);
  Form.EmailField.displayName = 'Form.EmailField';

  Form.EmailListField = memo(FormEmailListField<T>);
  Form.EmailListField.displayName = 'Form.EmailListField';

  Form.EmailTextField = memo(FormEmailTextField<T>);
  Form.EmailTextField.displayName = 'Form.EmailTextField';

  Form.FieldMappingDatePicker = memo(FormFieldMappingDatePicker<T>);
  Form.FieldMappingDatePicker.displayName = 'Form.FieldMappingDatePicker';

  Form.FieldMappingNumberField = memo(FormFieldMappingNumberField<T>);
  Form.FieldMappingNumberField.displayName = 'Form.FieldMappingNumberField';

  Form.FieldMappingPhoneField = memo(FormFieldMappingPhoneField<T>);
  Form.FieldMappingPhoneField.displayName = 'Form.FieldMappingPhoneField';

  Form.FieldMappingPhoneListField = memo(FormFieldMappingPhoneListField<T>);
  Form.FieldMappingPhoneListField.displayName = 'Form.FieldMappingPhoneListField';

  Form.FieldMappingSelectField = memo(FormFieldMappingSelectField<T>);
  Form.FieldMappingSelectField.displayName = 'Form.FieldMappingSelectField';

  Form.FieldMappingSelectMulti = memo(FormFieldMappingSelectMulti<T>);
  Form.FieldMappingSelectMulti.displayName = 'Form.FieldMappingSelectMulti';

  Form.FieldMappingField = memo(FormFieldMappingField<T>);
  Form.FieldMappingField.displayName = 'Form.FieldMappingField';

  Form.FieldMappingListField = memo(FormFieldMappingListField<T>);
  Form.FieldMappingListField.displayName = 'Form.FieldMappingListField';

  Form.FieldMappingFileField = memo(FormFieldMappingFileField<T>);
  Form.FieldMappingFileField.displayName = 'Form.FieldMappingFileField';

  Form.FieldMappingFileListField = memo(FormFieldMappingFileListField<T>);
  Form.FieldMappingFileListField.displayName = 'Form.FieldMappingFileListField';

  Form.FieldMappingTextEditor = memo(FormFieldMappingTextEditor);
  Form.FieldMappingTextEditor.displayName = 'Form.FieldMappingTextEditor';

  Form.FieldMappingJsonEditor = memo(FormFieldMappingJsonEditor);
  Form.FieldMappingJsonEditor.displayName = 'Form.FieldMappingJsonEditor';

  Form.DynamicFields = memo(FormDynamicFields);
  Form.DynamicFields.displayName = 'Form.DynamicFields';

  Form.JsonEditor = memo(FormJsonEditor<T>);
  Form.JsonEditor.displayName = 'Form.JsonEditor';

  Form.GraphQLEditor = memo(FormGraphQLEditor<T>);
  Form.GraphQLEditor.displayName = 'Form.GraphQLEditor';

  Form.PythonEditor = memo(FormPythonEditor<T>);
  Form.PythonEditor.displayName = 'Form.PythonEditor';

  Form.UrlField = memo(FormUrlField<T>);
  Form.UrlField.displayName = 'Form.UrlField';

  Form.PrefixedPostfixedTextField = memo(FormPrefixedPostfixedTextField<T>);
  Form.PrefixedPostfixedTextField.displayName = 'Form.PrefixedPostfixedTextField';

  return Form;
}
export type FormDataType = Record<string, any>;

export const Form = createForm<FormDataType>();

export type FormInstance<Data> = ReturnType<typeof createForm<Data>>;
